const EMPTY_NOTE_LABELS =
  /^(Feeding:|Transfers:|Toileting:|Mobility:|Ambulation:|Vitals:|Cognitive:|Notes:|\s)*$/i
const ALLOWED_HTML_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'EM',
  'B',
  'I',
  'UL',
  'OL',
  'LI',
  'DIV',
  'SPAN',
])

const PRINT_CSS = `
@page { size: letter; margin: 0.6in 0.55in 0.7in 0.55in; }
* { box-sizing: border-box; }
html, body {
  margin: 0;
  padding: 0;
  color: #2c2620;
  background: #fff;
  font: 10pt/1.4 system-ui, sans-serif;
}
h1 { font-size: 16pt; margin: 0 0 4px; }
h2 {
  font-size: 11pt;
  margin: 16px 0 8px;
  padding: 4px 8px;
  background: #f4eee6;
  border-left: 3px solid #8a4b2f;
}
.meta { color: #6b635a; font-size: 9pt; margin: 0 0 12px; }
.header-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 18px;
  margin: 12px 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #d9d0c4;
}
.block h3 {
  margin: 0 0 4px;
  font-size: 8pt;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b635a;
}
.block p { margin: 0 0 2px; }
table { width: 100%; border-collapse: collapse; margin: 0 0 8px; font-size: 9pt; }
th, td { border: 1px solid #d9d0c4; padding: 4px 6px; vertical-align: top; text-align: left; }
th { background: #faf6f0; }
.muted { color: #9a9188; }
td p { margin: 0 0 3px; }
`

export function ccdaToPrintableHtml(xml: string): string {
  const doc = new DOMParser().parseFromString(
    xml.replace(/^\uFEFF/, ''),
    'application/xml'
  )
  const root = doc.documentElement
  if (!root || root.localName !== 'ClinicalDocument') {
    throw new Error('Invalid C-CDA XML')
  }
  const title =
    textOf(findOne(root, 'title')) ||
    attr(findOne(root, 'code'), 'displayName') ||
    'Medical Summary'
  const effective = formatHl7Time(attr(findOne(root, 'effectiveTime'), 'value'))
  const patient = findOne(root, 'patient')
  const names = patient ? findAll(patient, 'name') : []
  const legal =
    names.find((name) => name.getAttribute('use') === 'L') ?? names[0]
  const aka = names.find((name) => name.getAttribute('use') === 'P')
  const patientName = personName(legal)
  const akaName = personName(aka)
  const genderEl = patient
    ? findOne(patient, 'administrativeGenderCode')
    : undefined
  const gender = attr(genderEl, 'displayName') || attr(genderEl, 'code') || ''
  const dob = formatHl7Time(attr(findOne(patient, 'birthTime'), 'value'))
  const patientRole = findOne(root, 'patientRole')
  const address = formatAddr(children(patientRole, 'addr')[0])
  const telecoms = children(patientRole, 'telecom')
    .map(formatTelecom)
    .filter(Boolean)
  const authorName = personName(
    findOne(findOne(root, 'assignedAuthor'), 'name')
  )
  const orgName = textOf(
    findOne(findOne(root, 'representedCustodianOrganization'), 'name')
  )

  const sections = findAll(root, 'section').map((section) => {
    const heading = textOf(findOne(section, 'title')) || 'Untitled section'
    const textEl = children(section, 'text')[0]
    return { heading, body: narrativeHtml(textEl) }
  })

  const akaHtml = akaName ? `<p class="muted">${escapeHtml(akaName)}</p>` : ''
  const sectionHtml = sections
    .map(
      (section) =>
        `<section><h2>${escapeHtml(section.heading)}</h2>${section.body}</section>`
    )
    .join('')

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(printFilename(patientName, title))}</title>
<style>${PRINT_CSS}</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">Seen Health C-CDA${effective ? ` · exported ${escapeHtml(effective)}` : ''}</p>
  <div class="header-grid">
    <div class="block">
      <h3>Patient</h3>
      <p><strong>${escapeHtml(patientName || '—')}</strong></p>
      ${akaHtml}
      <p>${escapeHtml([dob && `DOB ${dob}`, gender].filter(Boolean).join(' · ') || '—')}</p>
      <p>${address || '—'}</p>
      <p>${telecoms.map(escapeHtml).join('<br>') || '—'}</p>
    </div>
    <div class="block">
      <h3>Document</h3>
      <p>Author ${escapeHtml(authorName || '—')}</p>
      <p>Custodian ${escapeHtml(orgName || 'Seen Health')}</p>
    </div>
  </div>
  ${sectionHtml}
</body>
</html>`
}

export function printFilename(patientName: string, title: string): string {
  const name = patientName.trim() || 'CCDA'
  return `${name} - ${title}`
}

function narrativeHtml(textEl: Element | undefined): string {
  if (!textEl || (!textEl.childElementCount && !textEl.textContent?.trim())) {
    return ''
  }
  return serializeChildren(textEl)
}

function serializeChildren(el: Element): string {
  return [...el.childNodes].map(serializeNode).join('')
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return formatText(node.textContent ?? '')
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }
  const el = node as Element
  switch (el.localName) {
    case 'br':
      return '<br>'
    case 'paragraph':
    case 'content': {
      const inner = serializeChildren(el)
      return inner ? `<p>${inner}</p>` : ''
    }
    case 'list': {
      const items = children(el, 'item')
        .map((item) => `<li>${serializeChildren(item)}</li>`)
        .join('')
      const tag = el.getAttribute('listType') === 'ordered' ? 'ol' : 'ul'
      return `<${tag}>${items}</${tag}>`
    }
    case 'table':
      return serializeTable(el)
    case 'thead':
    case 'tbody':
    case 'tfoot':
    case 'tr':
    case 'th':
    case 'td':
    case 'caption':
      return serializeTablePart(el)
    default:
      return serializeChildren(el)
  }
}

function serializeTable(table: Element): string {
  const caption = children(table, 'caption')[0]
  const thead = children(table, 'thead')[0]
  const tbody = children(table, 'tbody')[0] ?? table
  const parts = ['<table>']
  if (caption && textOf(caption)) {
    parts.push(`<caption>${escapeHtml(textOf(caption))}</caption>`)
  }
  if (thead) {
    parts.push('<thead>', serializeRows(thead, 'th'), '</thead>')
  }
  parts.push('<tbody>', serializeRows(tbody, 'td'), '</tbody></table>')
  return parts.join('')
}

function serializeRows(parent: Element, cellTag: 'td' | 'th'): string {
  return children(parent, 'tr')
    .map((tr) => {
      const cells = [...tr.children].filter((cell) =>
        ['td', 'th'].includes(cell.localName)
      )
      if (cells.every((cell) => !cellText(cell))) {
        return ''
      }
      const rendered = cells
        .map((cell) => `<${cellTag}>${serializeCell(cell)}</${cellTag}>`)
        .join('')
      return `<tr>${rendered}</tr>`
    })
    .join('')
}

function serializeTablePart(el: Element): string {
  return serializeChildren(el)
}

function serializeCell(el: Element): string {
  if (el.childElementCount > 0) {
    return serializeChildren(el) || '—'
  }
  return formatText(el.textContent ?? '') || '—'
}

function formatText(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) {
    return ''
  }
  if (/<\/?[a-zA-Z]/.test(trimmed)) {
    const sanitized = sanitizeHtmlFragment(trimmed)
    const flattened = sanitized
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (!flattened || EMPTY_NOTE_LABELS.test(flattened)) {
      return '<span class="muted">—</span>'
    }
    return sanitized
  }
  return escapeHtml(trimmed)
}

function sanitizeHtmlFragment(markup: string): string {
  const parsed = new DOMParser().parseFromString(
    `<body>${markup}</body>`,
    'text/html'
  )
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent ?? '')
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return ''
    }
    const el = node as Element
    const inner = [...el.childNodes].map(walk).join('')
    if (!ALLOWED_HTML_TAGS.has(el.tagName)) {
      return inner
    }
    const tag = el.tagName.toLowerCase()
    if (tag === 'br') {
      return '<br>'
    }
    return `<${tag}>${inner}</${tag}>`
  }
  return [...parsed.body.childNodes].map(walk).join('')
}

function findAll(root: ParentNode | undefined, localName: string): Element[] {
  if (!root) {
    return []
  }
  if (root instanceof Document || root instanceof Element) {
    return [...root.getElementsByTagNameNS('*', localName)]
  }
  return [...root.querySelectorAll('*')].filter(
    (node) => node.localName === localName
  )
}

function findOne(
  root: ParentNode | undefined,
  localName: string
): Element | undefined {
  return findAll(root, localName)[0]
}

function children(el: Element | undefined, localName: string): Element[] {
  if (!el) {
    return []
  }
  return [...el.children].filter((child) => child.localName === localName)
}

function textOf(el: Element | undefined): string {
  return el?.textContent?.trim() ?? ''
}

function attr(el: Element | undefined, name: string): string {
  return el?.getAttribute(name) ?? ''
}

function personName(nameEl: Element | undefined): string {
  if (!nameEl) {
    return ''
  }
  const given = children(nameEl, 'given')
    .map((el) => textOf(el))
    .filter(Boolean)
    .join(' ')
  const family = textOf(children(nameEl, 'family')[0])
  return [given, family].filter(Boolean).join(' ')
}

function formatAddr(addr: Element | undefined): string {
  if (!addr) {
    return ''
  }
  const streets = children(addr, 'streetAddressLine')
    .map((el) => textOf(el))
    .filter(Boolean)
  const city = textOf(children(addr, 'city')[0])
  const state = textOf(children(addr, 'state')[0])
  const postal = textOf(children(addr, 'postalCode')[0])
  const cityLine = [city, [state, postal].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return [...streets, cityLine]
    .flatMap((line) => (line ? [escapeHtml(line)] : []))
    .join('<br>')
}

function formatTelecom(el: Element): string {
  const value = el.getAttribute('value') ?? ''
  if (value.startsWith('tel:')) {
    return `Phone: ${value.slice(4)}`
  }
  if (value.startsWith('mailto:')) {
    return `Email: ${value.slice(7)}`
  }
  if (value.startsWith('fax:')) {
    return `Fax: ${value.slice(4)}`
  }
  return value
}

function formatHl7Time(value: string): string {
  if (!value) {
    return ''
  }
  const raw = value.replace(/[+-]\d{4}$/, '')
  if (raw.length >= 8) {
    return `${Number(raw.slice(4, 6))}/${Number(raw.slice(6, 8))}/${raw.slice(0, 4)}`
  }
  return value
}

function cellText(el: Element): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}
